import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import GridTable from "../../components/GridTable";
import { errorMessage } from "../../components/Toaster";
import { getErrorMessage } from "../../utils/error";
import {
  getBayVisits,
  getCompanyBays,
  getLivePlantView,
  getTripJourney,
} from "./api";

const REFRESH_MS = 30000;
const DEFAULT_TARGET = 120;
const STUCK_MINUTES = 30;

const getCompanyId = () => localStorage.getItem("company_id");

const fmtMinutes = (m) => {
  if (m == null) return "—";
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
};

const fmtTime = (v) =>
  v
    ? new Date(v).toLocaleTimeString("en-IN", {
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const fmtDateTime = (v) =>
  v
    ? new Date(v).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const toInputDate = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const daysBack = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toInputDate(d);
};

const targetOf = (row) => row?.target_dwell_minutes || DEFAULT_TARGET;
const isDelayed = (m, target) => m >= target;

const dwellColor = (m, target = DEFAULT_TARGET) =>
  m >= target
    ? "#b91c1c"
    : m >= Math.round(target * 0.75)
      ? "#b45309"
      : "#15803d";

const visitStatus = (v) => {
  const delayed = isDelayed(v.dwell_minutes, targetOf(v));
  if (!v.exit_at && v.trip_status === "COMPLETED")
    return { label: "No exit scan", bg: "#fef3c7", color: "#92400e" };
  if (!v.exit_at)
    return delayed
      ? { label: "In bay · delayed", bg: "#fee2e2", color: "#991b1b" }
      : { label: "In bay", bg: "#dbeafe", color: "#1d4ed8" };
  return delayed
    ? { label: "Exited · delayed", bg: "#fee2e2", color: "#991b1b" }
    : { label: "Exited", bg: "#f1f5f9", color: "#475569" };
};

const cardSx = {
  background: "#fff",
  border: "1px solid #e3e8ef",
  borderRadius: "14px",
};
const labelSx = { fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.75 };
const inputSx = {
  background: "#fff",
  "& .MuiOutlinedInput-root": { height: 44, borderRadius: "8px" },
};
const headCellSx = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "#64748b",
};
const monoFont = "'IBM Plex Mono', ui-monospace, monospace";

const Pill = ({ label, bg, color }) => (
  <Typography
    component="span"
    sx={{
      fontSize: 12,
      fontWeight: 700,
      px: 1.25,
      py: 0.5,
      borderRadius: "999px",
      background: bg,
      color,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </Typography>
);

const StatTile = ({ label, value, color = "#0f172a", danger }) => (
  <Box
    sx={{
      ...cardSx,
      borderRadius: "12px",
      px: 2.25,
      py: 2,
      borderColor: danger ? "#fca5a5" : "#e3e8ef",
    }}
  >
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 600,
        color: danger ? "#991b1b" : "#64748b",
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: 28, fontWeight: 800, color, mt: 0.5 }}>
      {value}
    </Typography>
  </Box>
);

const InfoBox = ({ label, value, danger, mono }) => (
  <Box
    sx={{
      background: danger ? "#fef2f2" : "#f6f8fb",
      borderRadius: "10px",
      px: 2,
      py: 1.75,
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.6,
        color: danger ? "#991b1b" : "#64748b",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.5,
        fontSize: 16,
        fontWeight: danger ? 800 : 700,
        color: danger ? "#b91c1c" : "#0f172a",
        fontFamily: mono ? monoFont : "inherit",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const BayCard = ({ bay, onHistory }) => {
  const target = targetOf(bay);
  const hasDelay = bay.vehicles.some((v) => isDelayed(v.dwell_minutes, target));
  const lineColor = hasDelay ? "#fde2e2" : "#eef1f5";
  const count = bay.vehicles.length;
  const countLabel =
    bay.capacity > 1 ? `${count} / ${bay.capacity} inside` : `${count} inside`;

  return (
    <Box
      sx={{
        ...cardSx,
        p: "20px 22px",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        borderColor: hasDelay ? "#fca5a5" : "#e3e8ef",
        background: hasDelay ? "#fef2f2" : "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          pb: 1.25,
        }}
      >
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>
          {bay.bay_area_name}
        </Typography>
        {count === 0 ? (
          <Pill label="Free" bg="#f1f5f9" color="#475569" />
        ) : hasDelay ? (
          <Pill
            label={`${countLabel} · delayed`}
            bg="#fee2e2"
            color="#991b1b"
          />
        ) : (
          <Pill label={countLabel} bg="#dbeafe" color="#1d4ed8" />
        )}
      </Box>

      {count === 0 ? (
        <Typography
          sx={{
            fontSize: 14,
            color: "#64748b",
            py: 2,
            borderTop: `1px solid ${lineColor}`,
          }}
        >
          No vehicle in bay
        </Typography>
      ) : (
        bay.vehicles.map((v) => (
          <Box
            key={v.trip_id}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.25,
              borderTop: `1px solid ${lineColor}`,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: monoFont,
                  fontSize: 15,
                  fontWeight: 600,
                  color: "#0f172a",
                }}
              >
                {v.vehicle_number}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                In since {fmtTime(v.entry_at)}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 17,
                fontWeight: 800,
                color: dwellColor(v.dwell_minutes, target),
              }}
            >
              {fmtMinutes(v.dwell_minutes)}
            </Typography>
          </Box>
        ))
      )}

      <Box
        sx={{
          mt: "auto",
          pt: 1.5,
          borderTop: `1px solid ${lineColor}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#64748b" }}>
          Limit {fmtMinutes(target)}
        </Typography>
        <Button
          size="small"
          onClick={() => onHistory(bay.bay_area_id)}
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 36 }}
        >
          History →
        </Button>
      </Box>
    </Box>
  );
};

const LiveView = ({ live, loading, onHistory }) => {
  const bays = useMemo(() => {
    const map = new Map();
    live.bays.forEach((row) => {
      if (!map.has(row.bay_area_id)) {
        map.set(row.bay_area_id, {
          bay_area_id: row.bay_area_id,
          bay_area_name: row.bay_area_name,
          capacity: row.capacity,
          target_dwell_minutes: row.target_dwell_minutes,
          vehicles: [],
        });
      }
      if (row.trip_id) map.get(row.bay_area_id).vehicles.push(row);
    });
    return [...map.values()];
  }, [live]);

  const inBays = bays.reduce((n, b) => n + b.vehicles.length, 0);
  const delayed = bays.reduce(
    (n, b) =>
      n +
      b.vehicles.filter((v) => isDelayed(v.dwell_minutes, targetOf(b))).length,
    0,
  );
  const transit = live.in_transit;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
      {loading && <LinearProgress />}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 1.75,
        }}
      >
        <StatTile label="In plant now" value={inBays + transit.length} />
        <StatTile label="Inside bays" value={inBays} color="#1d4ed8" />
        <StatTile label="In transit" value={transit.length} color="#6d28d9" />
        <StatTile
          label="Over bay time limit"
          value={delayed}
          color="#b91c1c"
          danger
        />
      </Box>

      <Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 1,
            mb: 1.75,
          }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
            Bay Occupancy
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.75,
              fontSize: 13,
              color: "#475569",
            }}
          >
            <span>
              <b style={{ color: "#15803d" }}>●</b> on time
            </span>
            <span>
              <b style={{ color: "#b45309" }}>●</b> near limit
            </span>
            <span>
              <b style={{ color: "#b91c1c" }}>●</b> over limit (delayed)
            </span>
          </Box>
        </Box>
        {bays.length === 0 && !loading ? (
          <Typography sx={{ fontSize: 14, color: "#64748b" }}>
            No bays set up for this plant
          </Typography>
        ) : (
          <Grid container spacing={2.25}>
            {bays.map((bay) => (
              <Grid key={bay.bay_area_id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <BayCard bay={bay} onHistory={onHistory} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Box sx={{ ...cardSx, p: "22px 24px" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 1,
            mb: 1.75,
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}
            >
              Vehicles in Transit
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#64748b", mt: 0.5 }}>
              Inside the plant, moving between checkpoints and bays
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: "#475569" }}>
            Amber = not scanned for 30m+
          </Typography>
        </Box>

        {transit.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: "#64748b", py: 2 }}>
            No vehicles in transit
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: 760 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.1fr 2fr 1fr 1fr 1fr",
                  gap: 1.5,
                  py: 1.25,
                  borderBottom: "1px solid #e3e8ef",
                }}
              >
                {[
                  "Vehicle",
                  "Last seen at",
                  "Scanned",
                  "Since last scan",
                  "In plant for",
                ].map((h) => (
                  <Typography key={h} sx={headCellSx}>
                    {h}
                  </Typography>
                ))}
              </Box>
              {transit.map((v) => (
                <Box
                  key={v.trip_id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.1fr 2fr 1fr 1fr 1fr",
                    gap: 1.5,
                    py: 1.75,
                    alignItems: "center",
                    borderBottom: "1px solid #eef1f5",
                  }}
                >
                  <Typography
                    sx={{ fontFamily: monoFont, fontWeight: 600, fontSize: 14 }}
                  >
                    {v.vehicle_number}
                  </Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    {v.last_location}{" "}
                    <Box component="span" sx={{ color: "#64748b" }}>
                      ·{" "}
                      {v.last_scan_type === "BAY_EXIT"
                        ? "exited bay"
                        : "checkpoint"}
                    </Box>
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#475569" }}>
                    {fmtTime(v.last_scan_at)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color:
                        v.minutes_since_last_scan >= STUCK_MINUTES
                          ? "#b45309"
                          : "#15803d",
                    }}
                  >
                    {fmtMinutes(v.minutes_since_last_scan)}
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    {fmtMinutes(v.in_plant_minutes)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const historyColumns = [
  {
    field: "vehicle_number",
    headerName: "Vehicle",
    flex: 1,
    minWidth: 130,
    renderCell: (p) => (
      <span style={{ fontFamily: monoFont, fontWeight: 600 }}>
        {p.row.vehicle_number}
      </span>
    ),
  },
  { field: "bay_area_name", headerName: "Bay", flex: 1.4, minWidth: 180 },
  {
    field: "entry_at",
    headerName: "Entered bay",
    flex: 1,
    minWidth: 140,
    renderCell: (p) => fmtDateTime(p.row.entry_at),
  },
  {
    field: "exit_at",
    headerName: "Exited bay",
    flex: 1,
    minWidth: 140,
    renderCell: (p) => fmtDateTime(p.row.exit_at),
  },
  {
    field: "dwell_minutes",
    headerName: "Time in bay",
    flex: 0.8,
    minWidth: 110,
    renderCell: (p) => (
      <span
        style={{
          fontWeight: 700,
          color: dwellColor(p.row.dwell_minutes, targetOf(p.row)),
        }}
      >
        {fmtMinutes(p.row.dwell_minutes)}
      </span>
    ),
  },
  {
    field: "status_label",
    headerName: "Status",
    flex: 1,
    minWidth: 150,
    renderCell: (p) => {
      const s = visitStatus(p.row);
      return <Pill label={s.label} bg={s.bg} color={s.color} />;
    },
  },
];
const Timeline = ({ steps, target }) => (
  <Box>
    {steps.map((s) => {
      const isBay = s.scan_type !== "CHECKPOINT";
      const delayed =
        s.scan_type === "BAY_EXIT" && s.minutes_from_previous >= target;
      const gap =
        s.minutes_from_previous == null
          ? null
          : s.scan_type === "BAY_EXIT"
            ? `${fmtMinutes(s.minutes_from_previous)} in bay`
            : `${fmtMinutes(s.minutes_from_previous)} transit`;
      return (
        <Box key={s.tag_scan_id}>
          {gap && (
            <Box
              sx={{
                ml: "7px",
                borderLeft: `2px dashed ${delayed ? "#fca5a5" : "#cbd5e1"}`,
                py: 1,
                pl: "22px",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: delayed
                    ? "#b91c1c"
                    : s.scan_type === "BAY_EXIT"
                      ? "#6d28d9"
                      : "#475569",
                }}
              >
                {gap}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.75,
              ...(isBay && {
                background: "#f5f3ff",
                borderRadius: "10px",
                p: "10px 12px",
                ml: "-12px",
              }),
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                mt: "2px",
                borderRadius: "50%",
                flexShrink: 0,
                background: isBay ? "#6d28d9" : "#16a34a",
              }}
            />
            <Box>
              <Typography
                sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}
              >
                {s.location}
                {s.scan_type === "BAY_ENTRY"
                  ? " · entry"
                  : s.scan_type === "BAY_EXIT"
                    ? " · exit"
                    : ""}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                {fmtDateTime(s.scanned_at)} · {s.reader_serial}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    })}
  </Box>
);

const BayHistory = ({ initialBay }) => {
  const [bays, setBays] = useState([]);
  const [allVisits, setAllVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bayId, setBayId] = useState(initialBay || "all");
  const [startDate, setStartDate] = useState(daysBack(6));
  const [endDate, setEndDate] = useState(toInputDate(new Date()));
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [journey, setJourney] = useState([]);
  const [loadingJourney, setLoadingJourney] = useState(false);

  useEffect(() => {
    getCompanyBays(getCompanyId())
      .then((res) => setBays(res.data || []))
      .catch((err) =>
        errorMessage(getErrorMessage(err) || "Failed to load bays."),
      );
  }, []);

  useEffect(() => {
    if (!startDate || !endDate) return;
    setLoading(true);
    getBayVisits({
      companyId: getCompanyId(),
      startDate: new Date(`${startDate}T00:00:00`).toISOString(),
      endDate: new Date(`${endDate}T23:59:59`).toISOString(),
    })
      .then((res) => setAllVisits(res.data || []))
      .catch((err) =>
        errorMessage(getErrorMessage(err) || "Failed to load bay visits."),
      )
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  const visits = useMemo(
    () =>
      allVisits
        .filter((v) => bayId === "all" || v.bay_area_id === bayId)
        .map((v) => ({ ...v, status_label: visitStatus(v).label })),
    [allVisits, bayId],
  );

  const summary = useMemo(
    () =>
      bays
        .filter((b) => bayId === "all" || b.bay_area_id === bayId)
        .map((b) => {
          const rows = visits.filter((v) => v.bay_area_id === b.bay_area_id);
          const total = rows.reduce((n, v) => n + v.dwell_minutes, 0);
          return {
            ...b,
            count: rows.length,
            avg: rows.length ? Math.round(total / rows.length) : null,
            longest: rows.length
              ? Math.max(...rows.map((v) => v.dwell_minutes))
              : null,
            over: rows.filter((v) => isDelayed(v.dwell_minutes, targetOf(b)))
              .length,
          };
        }),
    [bays, visits, bayId],
  );

  const openVisit = (visit) => {
    setSelected(visit);
    setJourney([]);
    setLoadingJourney(true);
    getTripJourney(visit.trip_id)
      .then((res) => setJourney(res.data || []))
      .catch((err) =>
        errorMessage(getErrorMessage(err) || "Failed to load trip."),
      )
      .finally(() => setLoadingJourney(false));
  };

  const handleTableClick = (e) => {
    const rowEl = e.target.closest(".MuiDataGrid-row");
    if (!rowEl) return;
    const visit = visits.find(
      (v) => String(v.visit_id) === rowEl.getAttribute("data-id"),
    );
    if (visit) openVisit(visit);
  };

  const status = selected ? visitStatus(selected) : null;
  const selectedTarget = targetOf(selected);
  const selectedDelayed = selected
    ? isDelayed(selected.dwell_minutes, selectedTarget)
    : false;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography sx={labelSx}>Bay</Typography>
          <TextField
            select
            size="small"
            value={bayId}
            onChange={(e) => setBayId(e.target.value)}
            sx={{ ...inputSx, width: 230 }}
          >
            <MenuItem value="all">All bays</MenuItem>
            {bays.map((b) => (
              <MenuItem key={b.bay_area_id} value={b.bay_area_id}>
                {b.bay_area_name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box>
          <Typography sx={labelSx}>From</Typography>
          <TextField
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            sx={inputSx}
            slotProps={{ htmlInput: { max: endDate } }}
          />
        </Box>

        <Box>
          <Typography sx={labelSx}>To</Typography>
          <TextField
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            sx={inputSx}
            slotProps={{
              htmlInput: { min: startDate, max: toInputDate(new Date()) },
            }}
          />
        </Box>
        <Box>
          <Typography sx={labelSx}>Vehicle no</Typography>
          <TextField
            size="small"
            placeholder="e.g. HR39AB1234"
            value={vehicleSearch}
            onChange={(e) =>
              setVehicleSearch(e.target.value.toUpperCase().replace(/\s+/g, ""))
            }
            sx={{ ...inputSx, width: 230 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#64748b" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
          gap: 2,
        }}
      >
        {summary.map((b) => {
          const hasOver = b.over > 0;
          const stats = [
            { label: "Visits", value: b.count, color: "#0f172a" },
            {
              label: "Longest stay",
              value: fmtMinutes(b.longest),
              color: "#0f172a",
            },
            {
              label: "Over limit",
              value: b.over,
              color: hasOver ? "#b91c1c" : "#0f172a",
              danger: hasOver,
            },
          ];

          return (
            <Box
              key={b.bay_area_id}
              sx={{
                ...cardSx,
                p: "18px 20px",
                borderColor: hasOver ? "#fca5a5" : "#e3e8ef",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography
                  sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}
                >
                  {b.bay_area_name}
                </Typography>
                <Pill
                  label={`Limit ${fmtMinutes(targetOf(b))}`}
                  bg="#f1f5f9"
                  color="#475569"
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 1.25,
                }}
              >
                {stats.map((s) => (
                  <Box
                    key={s.label}
                    sx={{
                      background: s.danger ? "#fef2f2" : "#f6f8fb",
                      borderRadius: "10px",
                      px: 1.5,
                      py: 1.25,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                        color: s.danger ? "#991b1b" : "#64748b",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {s.label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 20,
                        fontWeight: 800,
                        color: s.color,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {s.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box
        onClick={handleTableClick}
        sx={{
          ...cardSx,
          overflow: "hidden",
          "& .MuiDataGrid-row": { cursor: "pointer" },
          ...(selected && {
            [`& .MuiDataGrid-row[data-id="${selected.visit_id}"]`]: {
              backgroundColor: "#eff6ff",
            },
          }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 1,
            px: 2.75,
            pt: 2.25,
            pb: 1,
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
            Bay visits
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#475569" }}>
            Click a row to see the visit and the truck's trip
          </Typography>
        </Box>
        {loading && <LinearProgress />}
        <GridTable
          rows={visits}
          columns={historyColumns}
          getRowId={(row) => row.visit_id}
          searchQuery={vehicleSearch}
          pageSize={10}
          density="standard"
        />
      </Box>

      <Drawer
        anchor="right"
        open={!!selected}
        onClose={() => setSelected(null)}
        sx={{
          "& .MuiDrawer-paper": {
            width: 500,
            maxWidth: "100%",
            p: "28px 32px",
            boxSizing: "border-box",
          },
        }}
      >
        {selected && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Box>
                <Box
                  sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}
                >
                  <Typography
                    sx={{ fontFamily: monoFont, fontSize: 24, fontWeight: 600 }}
                  >
                    {selected.vehicle_number}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                    {selected.vehicle_type}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    mt: 1.25,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Pill
                    label={status.label}
                    bg={status.bg}
                    color={status.color}
                  />
                  <Typography sx={{ fontSize: 13, color: "#475569" }}>
                    {selected.bay_area_name} ·{" "}
                    {new Date(selected.entry_at).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                aria-label="Close"
                onClick={() => setSelected(null)}
                sx={{
                  width: 44,
                  height: 44,
                  border: "1px solid #e3e8ef",
                  borderRadius: "8px",
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Grid container spacing={1.5}>
              <Grid size={6}>
                <InfoBox
                  label="ENTERED BAY"
                  value={fmtDateTime(selected.entry_at)}
                />
              </Grid>
              <Grid size={6}>
                <InfoBox
                  label="EXITED BAY"
                  value={
                    selected.exit_at
                      ? fmtDateTime(selected.exit_at)
                      : selected.trip_status === "COMPLETED"
                        ? "No exit scan"
                        : "Still inside"
                  }
                />
              </Grid>
              <Grid size={6}>
                <InfoBox
                  label="TIME IN BAY"
                  danger={selectedDelayed}
                  value={
                    selectedDelayed
                      ? `${fmtMinutes(selected.dwell_minutes)} (${fmtMinutes(selected.dwell_minutes - selectedTarget)} over)`
                      : fmtMinutes(selected.dwell_minutes)
                  }
                />
              </Grid>
              <Grid size={6}>
                <InfoBox
                  label="BAY SCANNER"
                  value={selected.reader_serial}
                  mono
                />
              </Grid>
            </Grid>

            <Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: "#64748b",
                  mb: 1.75,
                }}
              >
                FULL TRIP
              </Typography>
              {loadingJourney ? (
                <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                  Loading trip…
                </Typography>
              ) : journey.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                  No scans recorded for this trip.
                </Typography>
              ) : (
                <Timeline steps={journey} target={selectedTarget} />
              )}
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

const PlantHome = () => {
  const [view, setView] = useState("live");
  const [historyBay, setHistoryBay] = useState("all");
  const [live, setLive] = useState({ bays: [], in_transit: [] });
  const [loadingLive, setLoadingLive] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const refresh = useCallback(() => {
    setLoadingLive(true);
    getLivePlantView(getCompanyId())
      .then((res) => {
        setLive({
          bays: res.data?.bays || [],
          in_transit: res.data?.in_transit || [],
        });
        setUpdatedAt(new Date());
      })
      .catch((err) =>
        errorMessage(getErrorMessage(err) || "Failed to load plant view."),
      )
      .finally(() => setLoadingLive(false));
  }, []);

  useEffect(() => {
    if (view !== "live") return;
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [view, refresh]);

  const openHistory = (bayId) => {
    setHistoryBay(bayId || "all");
    setView("history");
  };

  const tabSx = (active) => ({
    height: 40,
    px: 2.25,
    borderRadius: "7px",
    textTransform: "none",
    fontWeight: 600,
    fontSize: 14,
    color: active ? "#fff" : "#334155",
    background: active ? "#0f172a" : "transparent",
    "&:hover": { background: active ? "#0f172a" : "#f1f5f9" },
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 800, color: "#0f172a" }}>
            Plant View
          </Typography>
          <Typography sx={{ fontSize: 15, color: "#64748b", mt: 0.5 }}>
            {view === "live"
              ? "Current status of every bay and vehicle"
              : "Every vehicle that entered a bay in the selected dates"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {view === "live" && (
            <>
              {updatedAt && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: 13,
                    color: "#475569",
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#16a34a",
                    }}
                  />
                  Updated{" "}
                  {updatedAt.toLocaleTimeString("en-IN", {
                    hour: "numeric",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </Box>
              )}
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refresh}
                disabled={loadingLive}
                sx={{
                  height: 44,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#0f172a",
                  borderColor: "#d5dce6",
                  background: "#fff",
                }}
              >
                Refresh
              </Button>
            </>
          )}

          <Box
            role="tablist"
            aria-label="View"
            sx={{
              display: "flex",
              gap: 0.5,
              background: "#fff",
              border: "1px solid #d5dce6",
              borderRadius: "10px",
              p: 0.5,
            }}
          >
            <Button
              role="tab"
              aria-selected={view === "live"}
              onClick={() => setView("live")}
              sx={tabSx(view === "live")}
            >
              Live
            </Button>
            <Button
              role="tab"
              aria-selected={view === "history"}
              onClick={() => openHistory("all")}
              sx={tabSx(view === "history")}
            >
              Bay History
            </Button>
          </Box>
        </Box>
      </Box>

      {view === "live" ? (
        <LiveView live={live} loading={loadingLive} onHistory={openHistory} />
      ) : (
        <BayHistory key={historyBay} initialBay={historyBay} />
      )}
    </Box>
  );
};

export default PlantHome;
